import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Attribute } from './Attribute';
import { IAttribute, AttributeType } from './IAttribute';
import { FormGroup, FormArray } from '@angular/forms';
import { INode } from '../Configuration/INode';
import { ConfigDetailComponent } from '../Configuration/config-detail.component';
import { ApplicationService } from '../application-service/application.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'wbm-attribute-polymorph',
    templateUrl: './attribute-polymorph.component.html',
    styleUrls: ['../../assets/theme/css/attribute.css']
})
export class AttributePolymorphComponent implements OnInit, OnDestroy {
    @Input() node: INode;
    @Input() index: String;
    @Input() group: FormGroup;
    @Input() controlName: string;
    @Input()
    set attribute(attribute: IAttribute) {
        this.isDisplayed = !attribute.isPassword;
        this._attribute = attribute;
    };

    get attribute(): IAttribute {
        return this._attribute;
    }

    get attributeStructureTypeValues(): string[] {
        if (!this.attribute.structure || !this.attribute.structure['type'] || !this.attribute.structure['type']['values']) {
            return [];
        } else {
            return this.attribute.structure['type']['values'].sort(this.nodeSortOnTranslatedStrings.bind(this));
        }
    }

    private _attribute: IAttribute;
    attributeType = AttributeType;
    isDisplayed: boolean;
    private configDetailWidth: number;
    private subscriptions: Array<Subscription> = [];

    private nodeSortOnTranslatedStrings(a: string, b: string): number {
        this._translate.get(a).subscribe((translatedString) => a = translatedString.toLowerCase());
        this._translate.get(b).subscribe((translatedString) => b = translatedString.toLowerCase());
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    }

    constructor(private _application: ApplicationService, private _translate: TranslateService) { }

    ngOnInit() {
        this.subscriptions.push(this._application.configWidthValueChanges().subscribe(result => this.configDetailWidth = result));
    }

    ngOnDestroy() {
        //release global subscriptions
        this.removeGlobalSubscriptions();
    }

    private removeGlobalSubscriptions() {
        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().unsubscribe();
        }
    }

    toggleInputValueVisibility(): void {
        this.isDisplayed = !this.isDisplayed;
    }

    addItemInSet(): void {
        if (this.attribute.type === AttributeType.Set_Of) {
            if (!this.attribute.value) {
                this.attribute.value = [];
            }
            //create one new attribute (without name) with the correct type (subtype of set_of)
            let newAttr = Attribute.create(undefined, { type: this.attribute.structure['type']['set_of'] }, this.attribute.isSettable);
            newAttr.isLocal = true;

            //add this new attribute in the attribute value array of the 'set_of' attribute
            this.attribute.value.push(newAttr);

            const setOfAttrControl: FormArray = this.group.controls[this.controlName] as FormArray;
            if (setOfAttrControl) {
                // Create control for new attribute and push it in 'set_of' attribute's form array
                setOfAttrControl.push(ConfigDetailComponent.getAttributeControls(this.node, newAttr, false, null));
                // Force the control value update to have a localValue correctly set in attribute
                setOfAttrControl.updateValueAndValidity();
            }
        }
    }

    removeItemFromSet(attr: IAttribute): void {
        if (this.attribute.type === AttributeType.Set_Of) {
            if (attr.isLocal) {
                //remove the attr from the attribute value array
                this.attribute.value.splice(this.attribute.value.indexOf(attr), 1);
            } else {
                attr.toDelete = true;
            }
            //Force the recomputing of the form's value,
            //so that the localValue of the 'set_of' attribute is defined and the submit button is activated
            let setOfAttrControl = this.group.controls[this.controlName];
            if (setOfAttrControl) {
                setOfAttrControl.updateValueAndValidity();
            }
        }
    }

    /**
     * Method used to filter bytestrings that are named 'Unused_xx'
     *
     * @param {string} name
     * @returns {boolean} true if it matches the pattern Unused_xx, false otherwise
     *
     * @memberOf AttributePolymorphComponent
     */
    isUnusedAttribute(name: string): boolean {
        let patternRegEx = /^Unused_[0-9]+$/i;
        return patternRegEx.test(name);
    }
}
